const escapeHtml = value =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

// Plain-text fallback for a message's related documents: not a real table
// (that's what the HTML version below is for - see copyToClipboard), just
// a readable, tab-separated list so it's still useful pasted anywhere that
// isn't a rich-text target.
const documentsListText = documents => {
  if (!documents || !documents.length) return "";
  const rows = documents.map(doc =>
    [doc.code, doc.title, doc.type, doc.url].filter(Boolean).join(" - ")
  );
  return ["", "Related documents:", ...rows].join("\n");
};

export const documentsTableHtml = documents => {
  if (!documents || !documents.length) return "";
  const rows = documents
    .map(doc => {
      const label = escapeHtml(doc.title || doc.code || "");
      const link = doc.url
        ? `<a href="${escapeHtml(doc.url)}">${label}</a>`
        : label;
      return `<tr><td>${escapeHtml(doc.code)}</td><td>${link}</td><td>${escapeHtml(
        doc.type
      )}</td></tr>`;
    })
    .join("");
  return (
    `<p><strong>Related documents:</strong></p>` +
    `<table border="1" cellspacing="0" cellpadding="4" style="border-collapse:collapse;">` +
    `<thead><tr><th>Code</th><th>Document</th><th>Type</th></tr></thead>` +
    `<tbody>${rows}</tbody></table>`
  );
};

// "User:"/"Myracle Bot:" plain-text transcript, matching the same prefixes
// used by MessageBubble, for the per-message and whole-session copy
// controls.
export const formatMessage = message =>
  message.role === "user"
    ? `User: ${message.text}`
    : `Myracle Bot: ${message.answer || ""}${documentsListText(message.documents)}`;

export const formatTranscript = messages =>
  messages.map(formatMessage).join("\n\n");

// Rich-text (HTML) counterparts of the two functions above, used alongside
// the plain-text version so pasting into Word/Outlook/Google Docs keeps
// formatting (bold prefixes, an actual <table> of related documents) -
// see copyToClipboard.
export const formatMessageHtml = message => {
  if (message.role === "user") {
    return `<p><strong>User:</strong> ${escapeHtml(message.text)}</p>`;
  }
  const answerHtml = escapeHtml(message.answer || "").replace(/\n/g, "<br/>");
  return (
    `<p><strong>Myracle Bot:</strong> ${answerHtml}</p>` +
    documentsTableHtml(message.documents)
  );
};

export const formatTranscriptHtml = messages =>
  messages.map(formatMessageHtml).join("<hr/>");

// Writes both text/plain and text/html to the clipboard when possible, so
// pasting into a rich-text target (Word, Outlook, Google Docs) keeps
// formatting instead of dropping in as flat text - browsers/Office apps
// treat an HTML clipboard payload as rich text on paste, which is the
// standard way to get "paste as RTF"-equivalent results without hand-
// building an actual .rtf file. Falls back to plain text wherever the
// richer API isn't available (older browsers, insecure contexts, denied
// clipboard permission).
export const copyToClipboard = async (text, html) => {
  if (html && navigator.clipboard?.write && window.ClipboardItem) {
    try {
      await navigator.clipboard.write([
        new window.ClipboardItem({
          "text/plain": new Blob([text], { type: "text/plain" }),
          "text/html": new Blob([html], { type: "text/html" }),
        }),
      ]);
      return;
    } catch (e) {
      // Fall through to the plain-text paths below.
    }
  }
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  // Fallback for browsers/contexts without the async clipboard API.
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
};

export const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
